import { FC } from 'react';

import { useDataManagementContext } from 'modules/account/settings/containers/DataManagement';
import {
  useCreateData,
  useGetData,
  useUpdateData,
} from 'modules/account/settings/hooks';
import * as Yup from 'yup';
import {
  DATA_SOURCE,
  DATA_TYPE,
  DATA_NUMBER_FORMAT,
  DATA_DATE_FORMAT,
  DATA_STRING_FORMAT,
} from 'utils/types';
import { useQueryClient } from '@tanstack/react-query';
import { uniqueId } from 'lodash';
import { Formik, type FormikHelpers } from 'formik';
import { CreateDataPayload } from 'modules/account/settings/hooks/useCreateData';
import constants from 'modules/account/settings/utils/constants';
import CreateFieldFormPopover from 'modules/account/settings/forms/CreateFieldFormPopover';

import { toastSuccess } from '../Toast';

const CREATE_FIELD_SCHEMA = Yup.object({
  name: Yup.string().required('Required'),
  source: Yup.string().oneOf([
    DATA_SOURCE.LEAD,
    DATA_SOURCE.COMPANY,
    DATA_SOURCE.DEAL,
    DATA_SOURCE.TASK,
  ]),
  description: Yup.string().optional().ensure(),
  type: Yup.string().oneOf([
    DATA_TYPE.STRING,
    DATA_TYPE.MOBILENUMBER,
    DATA_TYPE.NUMBER,
    DATA_TYPE.BOOLEAN,
    DATA_TYPE.SELECT,
    DATA_TYPE.MULTISELECT,
    DATA_TYPE.CHECKBOX,
    DATA_TYPE.RADIOBOX,
    DATA_TYPE.DATE,
  ]),
  options: Yup.array().of(
    Yup.object({
      id: Yup.string().required(),
      label: Yup.string().ensure(),
      value: Yup.string().ensure(),
    }),
  ),
  sortOptionsBy: Yup.string().oneOf(['alphabetical', 'custom']).required(),

  numberFormat: Yup.string().oneOf([
    DATA_NUMBER_FORMAT.UNFORMATTED,
    DATA_NUMBER_FORMAT.FORMATTED,
    DATA_NUMBER_FORMAT.CURRENCY,
    DATA_NUMBER_FORMAT.PERCENTAGE,
  ]),
  dateFormat: Yup.string().oneOf([
    DATA_DATE_FORMAT.DATE,
    DATA_DATE_FORMAT.DATETIME,
  ]),
  stringFormat: Yup.string()
    .oneOf([DATA_STRING_FORMAT.STRING, DATA_STRING_FORMAT.LONGSTRING])
    .nullable()
    .optional(),
  rules: Yup.object({
    setMinLimit: Yup.boolean().nullable(),
    setMaxLimit: Yup.boolean().nullable(),
    min: Yup.number().nullable().optional(),
    max: Yup.number().nullable().optional(),
  }),
  isInUse: Yup.boolean().optional(),
});

export type CREATE_FIELD_FORM = Yup.InferType<typeof CREATE_FIELD_SCHEMA>;

type ComposerFieldBodyProps = {
  mode: 'popover' | 'modal';
};

const ComposerFieldBody: FC<ComposerFieldBodyProps> = ({ mode }) => {
  const { selectedFieldId, handleClose } = useDataManagementContext();
  const { mutate: createData } = useCreateData();
  const { mutate: updateData } = useUpdateData();

  const { data: field } = useGetData(selectedFieldId as string);

  const queryClient = useQueryClient();

  const initialValues: CREATE_FIELD_FORM = {
    description: field?.description ?? '',
    name: field?.name ?? '',
    source: field?.source ?? undefined,
    type: field?.type ?? undefined,
    options: field?.meta.options?.length
      ? field?.meta.options.map((option) => ({
          label: option.label,
          value: option.value,
          id: uniqueId('option_'),
        }))
      : [{ id: uniqueId('option_'), label: '', value: '' }],
    sortOptionsBy: 'custom',
    dateFormat:
      field?.type === DATA_TYPE.DATE
        ? (field?.meta.format as DATA_DATE_FORMAT)
        : DATA_DATE_FORMAT.DATE,
    numberFormat:
      field?.type === DATA_TYPE.NUMBER
        ? (field?.meta.format as DATA_NUMBER_FORMAT)
        : DATA_NUMBER_FORMAT.FORMATTED,
    stringFormat:
      field?.type === DATA_TYPE.STRING
        ? (field?.meta.format as DATA_STRING_FORMAT)
        : DATA_STRING_FORMAT.STRING,
    rules: {
      min: field?.meta.min,
      max: field?.meta.max,
      setMinLimit: !!field?.meta.min,
      setMaxLimit: !!field?.meta.max,
    },
    isInUse: !!field?.totalAssociatedId,
  };

  const onSubmit = (
    values: CREATE_FIELD_FORM,
    { setSubmitting, resetForm }: FormikHelpers<CREATE_FIELD_FORM>,
  ) => {
    const castValues = CREATE_FIELD_SCHEMA.cast(values);
    const arrayTypes = [
      DATA_TYPE.MULTISELECT,
      DATA_TYPE.SELECT,
      DATA_TYPE.RADIOBOX,
      DATA_TYPE.CHECKBOX,
    ];

    const formattedTypes = [DATA_TYPE.NUMBER, DATA_TYPE.DATE, DATA_TYPE.STRING];

    const isArrayType = arrayTypes.includes(castValues.type as DATA_TYPE);
    const isFormattedType = formattedTypes.includes(
      castValues.type as DATA_TYPE,
    );

    const getFormat = (type: DATA_TYPE) => {
      switch (type) {
        case DATA_TYPE.STRING: {
          return castValues.stringFormat;
        }
        case DATA_TYPE.NUMBER: {
          return castValues.numberFormat;
        }
        case DATA_TYPE.DATE: {
          return castValues.dateFormat;
        }
      }
    };

    const payload: CreateDataPayload = {
      name: castValues.name,
      description: castValues.description,
      meta: {
        ...(isArrayType && {
          options: castValues.options?.map(({ label, value }) => ({
            label,
            value,
          })),
        }),
        ...(isFormattedType && {
          format: getFormat(castValues.type as DATA_TYPE) as
            | DATA_NUMBER_FORMAT
            | DATA_DATE_FORMAT
            | DATA_STRING_FORMAT,
        }),
        ...((castValues.type === DATA_TYPE.STRING ||
          castValues.type === DATA_TYPE.NUMBER) &&
          castValues.rules.setMinLimit && { min: castValues.rules.min ?? 1 }),

        ...((castValues.type === DATA_TYPE.STRING ||
          castValues.type === DATA_TYPE.NUMBER) &&
          castValues.rules.setMaxLimit && { max: castValues.rules.max ?? 100 }),
      },

      source: castValues.source as DATA_SOURCE,
      type: castValues.type as DATA_TYPE,
    };

    if (!selectedFieldId) {
      createData(payload, {
        onSuccess() {
          queryClient.invalidateQueries([constants.FIELDS]);

          toastSuccess('Successfuly Created a Custom Field');
          resetForm();
          handleClose?.();
        },
        onSettled() {
          setSubmitting(false);
        },
      });
    } else {
      updateData(
        { ...payload, id: selectedFieldId },
        {
          onSuccess() {
            queryClient.invalidateQueries([constants.FIELDS]);

            toastSuccess('Successfuly Updated a Custom Field');
            resetForm();
            handleClose?.();
          },
          onSettled() {
            setSubmitting(false);
          },
        },
      );
    }
  };

  const validate = (values: CREATE_FIELD_FORM) => {
    const optionErrors = values?.options?.reduce(
      (acc: { label: string; value: string }[], curr, index, arr) => {
        // check label in current item if it has duplicates in other parts of array
        const hasDuplicateLabel = arr.filter(
          (val, i) => i !== index && val.label === curr.label,
        );
        const hasDuplicateValue = arr.filter(
          (val, i) => i !== index && val.value === curr.value,
        );

        acc.push({
          label: hasDuplicateLabel.length ? 'Duplicate Label' : '',
          value: hasDuplicateValue.length ? 'Duplicate Value' : '',
        });
        return acc;
      },
      [],
    );
    const errors = {
      ...(optionErrors?.length ? { options: optionErrors } : {}),
    };

    // check if errors object has empty strings all in all
    //and remove the error.options object itself

    const checkIfAllOptionsNoErrors = errors.options?.every(
      (op) => op.label === '' && op.value === '',
    );

    if (checkIfAllOptionsNoErrors) {
      return {};
    }

    return errors;
  };

  return (
    <Formik
      enableReinitialize
      validate={validate}
      validationSchema={CREATE_FIELD_SCHEMA}
      onSubmit={onSubmit}
      initialValues={initialValues}
    >
      {() => <CreateFieldFormPopover mode={mode} />}
    </Formik>
  );
};

export default ComposerFieldBody;
